import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs';
import { Action } from 'src/app/core/models/action.model';
import { AllDoneDialogComponent } from './components/all-done-dialog/all-done-dialog.component';
import { HasUnsavedChanges } from 'src/app/core/guards/unsaved-changes.guard';
import { QuestionnaireAnswerComponent } from 'src/app/shared/components/smart/questionnaire-answer/questionnaire-answer.component';
import { GuideService } from './services/guide.service';
import { ManagementStepService } from './services/managementStep.service';
import { WizardLoadingService } from './services/wizard-loading.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { UserService } from 'src/app/core/services/user.service';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { WizardLoadingMessages } from './services/wizard-loading.service';
import { TutorialVideoService } from '../../shared/smart/welcome-popup/tutorial-video.service';
import { TUTORIAL_STEP_TIMESTAMPS } from '../../shared/smart/welcome-popup/tutorial-video.constants';
import { notesAllowProceed, isAgentSkipCheck } from 'src/app/core/utils/kyc-postpone.util';
import { DeferCaseActionService } from 'src/app/core/services/defer-case-action.service';
import { ToastService } from 'angular-toastify';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-guide',
  templateUrl: './guide.component.html',
  styleUrls: ['./guide.component.scss'],
})
export class GuideComponent implements HasUnsavedChanges {
  @ViewChild(QuestionnaireAnswerComponent) private questionnaireAnswerRef?: QuestionnaireAnswerComponent;

  claimId?: string;

  selectedActionIndex = 0;

  actions?: Action[];
  loaded: boolean = false;

  /** When set, the guide is in "skip guide" mode — only shows a specific questionnaire */
  private agentUploadQuestionnaireId: string | null = null;

  isDeferringAction = false;

  // Getter para forçar re-renderização de componentes quando selectedActionIndex muda
  get currentActionKey(): string {
    if (!this.actions || !this.actions[this.selectedActionIndex]) {
      return '';
    }
    // Combina index + ID único para garantir re-renderização
    const action = this.actions[this.selectedActionIndex];
    return `${this.selectedActionIndex}-${action.action_id}-${action.questionnaire_id || action.contract_id || ''}`;
  }

  /**
   * TrackBy function para forçar re-criação do componente quando a action muda
   * Retorna uma chave única baseada no index e IDs da action
   */
  trackByActionKey(index: number, action: Action): string {
    return `${this.selectedActionIndex}-${action.action_id}-${action.questionnaire_id || action.contract_id || ''}`;
  }

  hasUnsavedChanges(): boolean {
    return this.questionnaireAnswerRef?.hasUnsavedChanges() ?? false;
  }

  constructor(
    private route: ActivatedRoute,
    private guideService: GuideService,
    private managementStepService: ManagementStepService,
    private wizardLoadingService: WizardLoadingService,
    private router: Router,
    private dialog: MatDialog,
    private claimsService: ClaimsService,
    private kycService: KycService,
    private userService: UserService,
    private agentClientsService: AgentClientsService,
    private tutorialVideoService: TutorialVideoService,
    private deferCaseActionService: DeferCaseActionService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.route.params
      .pipe(
        tap(async (params) => {
          this.claimId = params['claimId'];
          // ✅ Remove verificação duplicada - guard já verificou
          // Carrega actions diretamente
          this.loadActions();
        })
      )
      .subscribe();
  }

  /**
   * CPK-4846: the Simple Questionnaire session now opens the claim's guide, so the
   * agent reaches KYC/SIGNATURE here — not on home, where the CPK-4745 "Continue
   * later" buttons live (and the guide guard keeps home locked while actions are
   * pending). Same defer endpoint (CPK-4748); ManagementStepService already treats
   * a deferred action as complete, so onComplete() moves on to the next step.
   */
  get canDeferCurrentAction(): boolean {
    const action = this.actions?.[this.selectedActionIndex];
    if (!action || !action.case_action_id || !action.claim_id) return false;
    if (action.action_type !== 'KYC' && action.action_type !== 'SIGNATURE') return false;
    return this.agentClientsService.isSimpleQuestionnaireSession();
  }

  async deferCurrentAction(): Promise<void> {
    const action = this.actions?.[this.selectedActionIndex];
    if (!action || !this.canDeferCurrentAction || this.isDeferringAction) return;

    this.isDeferringAction = true;
    try {
      const userId = (await this.userService.getCurrentUser())?.id;
      if (!userId) return;

      await this.deferCaseActionService.defer(action.case_action_id!, userId, action.claim_id!);
    } catch (error) {
      console.error('[GUIDE] Error deferring action (CPK-4846)', error);
      this.toastService.error(this.translate.instant('DeferCaseActionError'));
      this.isDeferringAction = false;
      return;
    }

    try {
      await this.onComplete();
    } finally {
      this.isDeferringAction = false;
    }
  }

  private resolveStepTimestamp(index: number): number {
    const action = this.actions?.[index];
    if (!action) return TUTORIAL_STEP_TIMESTAMPS.HOME;
    if (action.action_type === 'KYC') return TUTORIAL_STEP_TIMESTAMPS.KYC;
    if (action.action_type === 'SIGNATURE') return TUTORIAL_STEP_TIMESTAMPS.SIGNATURE;
    if (action.action_type === 'QUESTIONNAIRE') {
      const questionnairesBefore = (this.actions ?? [])
        .slice(0, index)
        .filter(a => a.action_type === 'QUESTIONNAIRE').length;
      if (questionnairesBefore === 0) return TUTORIAL_STEP_TIMESTAMPS.ELIGIBILITY_QUESTIONNAIRE;
      if (questionnairesBefore === 1) return TUTORIAL_STEP_TIMESTAMPS.IDENTIFICATION_QUESTIONNAIRE;
      return TUTORIAL_STEP_TIMESTAMPS.CONSENTS;
    }
    return TUTORIAL_STEP_TIMESTAMPS.HOME;
  }

  async loadActions() {
    this.loaded = false;

    // 🔄 Inicia loading com steps do INIT
    this.wizardLoadingService.start(
      WizardLoadingService.CommonSteps.INIT,
      WizardLoadingMessages.LOADING_WIZARD
    );

    this.wizardLoadingService.completeStep('guard');

    // ✅ AGORA carrega actions aqui (as 3 requisições do getOutstandingActionsV3)
    // Se já estiverem carregadas PRA ESSA CLAIM, reutiliza; senão, busca
    // (o cache é compartilhado entre claims — sem checar o id, o guide de uma
    // claim mostraria as actions cacheadas de outra, num usuário multi-claim)
    if (
      !this.guideService.requirementsFormClaim ||
      this.guideService.requirementsFormClaimId !== this.claimId
    ) {
      console.log('[GUIDE] Actions not loaded yet for this claim, fetching from service');
      await this.guideService.checkIfUserMustAccessGuide('all', this.claimId);
    }

    this.actions = this.guideService.requirementsFormClaim!;
    console.log('🔎 [WIZARD_DEBUG] GUIDE_ACTIONS_LOADED', {
      totalActions: this.actions?.length || 0,
      order: this.actions?.map((a, idx) => ({
        step: idx,
        name: a.action_name,
        type: a.action_type,
        caseActionId: a.case_action_id?.slice(0,8),
        prstatus: a.prstatus,
        prerequisite_ids: (a.prerequisite_ids || []).map(id => id.slice(0,8)),
        ac_status: a.ac_status,
      }))
    });

    this.wizardLoadingService.completeStep('actions');

    // ✅ Se não há actions, redireciona para home
    if (!this.actions || this.actions.length === 0) {
      console.log('[GUIDE] No actions found, redirecting to home');
      this.wizardLoadingService.hide();
      this.router.navigateByUrl('/client/home');
      return;
    }

    // ✅ Usa ManagementStepService para encontrar a primeira action incompleta de forma otimizada
    if (this.actions && this.actions.length > 0 && this.claimId) {
      const user = await this.userService.getCurrentUser();

      if (!user?.id) {
        console.error('[GUIDE] No user found');
        this.wizardLoadingService.hide();
        this.router.navigateByUrl('/client/home');
        return;
      }

      this.wizardLoadingService.completeStep('claim');
      this.wizardLoadingService.completeStep('prerequisites');

      const wizardState = await this.managementStepService.getState(
        this.actions,
        this.claimId,
        user.id
      );

      this.wizardLoadingService.completeStep('state');

      console.log('🔎 [WIZARD_DEBUG] INIT_WIZARD_STATE', {
        currentActionIndex: wizardState.currentActionIndex,
        currentAction: {
          name: wizardState.currentAction?.action_name,
          type: wizardState.currentAction?.action_type,
          caseActionId: wizardState.currentAction?.case_action_id?.slice(0,8),
          ac_status: wizardState.currentAction?.ac_status,
          prstatus: wizardState.currentAction?.prstatus,
        },
        allActionsComplete: wizardState.allActionsComplete,
        claimIsActive: wizardState.claimIsActive,
        selectedActionIndex: this.selectedActionIndex,
      });

      // Verifica se claim ficou inativo
      if (!wizardState.claimIsActive) {
        console.warn('[GUIDE] Claim is INACTIVE, redirecting to home');
        this.wizardLoadingService.hide();
        this.router.navigate(['/client/home'], {
          queryParams: { inactiveClaim: 'true', inactiveClaimId: this.claimId }
        });
        return;
      }

      // Check if agent wants to skip to a specific questionnaire. Runs before the
      // all-complete redirect below: optional documents are usually sent after the
      // rest of the claim is done, and the agent picked this target on purpose (CPK-4900).
      let agentTargetIndex = -1;
      this.agentUploadQuestionnaireId = localStorage.getItem('agentUploadQuestionnaireId');
      if (this.agentUploadQuestionnaireId && this.actions) {
        agentTargetIndex = this.actions.findIndex(
          (a) => a.action_type === 'QUESTIONNAIRE' && a.questionnaire_id === this.agentUploadQuestionnaireId
        );
        if (agentTargetIndex < 0) {
          console.warn('[GUIDE] Agent skipGuide questionnaire not found in actions, clearing', {
            questionnaireId: this.agentUploadQuestionnaireId,
          });
          localStorage.removeItem('agentUploadQuestionnaireId');
          this.agentUploadQuestionnaireId = null;
        }
      }

      // Se todas as actions já estavam completas ANTES de entrar no guide
      // (ex: chegou via redirect de um case já concluído), manda para a
      // página do claim ("My Claim") em vez de home (CPK-4171).
      if (wizardState.allActionsComplete && agentTargetIndex < 0) {
        console.log('[GUIDE] All actions complete on init, redirecting to claim page', {
          claimId: this.claimId,
        });
        this.wizardLoadingService.hide(); // Esconde imediatamente sem delay
        this.loaded = true;
        this.router.navigate([`/client/claim/${this.claimId}`]);
        return;
      }

      this.selectedActionIndex = wizardState.currentActionIndex;

      if (agentTargetIndex >= 0) {
        console.log('[GUIDE] Agent skipGuide mode — jumping to questionnaire', {
          questionnaireId: this.agentUploadQuestionnaireId,
          targetIndex: agentTargetIndex,
          allActionsComplete: wizardState.allActionsComplete,
        });
        this.selectedActionIndex = agentTargetIndex;
      }

      this.tutorialVideoService.setStepTimestamp(this.resolveStepTimestamp(this.selectedActionIndex));

      console.log('🔎 [WIZARD_DEBUG] INITIAL_STEP_SELECTED', {
        selectedActionIndex: this.selectedActionIndex,
        selectedAction: {
          name: this.actions?.[this.selectedActionIndex]?.action_name,
          type: this.actions?.[this.selectedActionIndex]?.action_type,
          caseActionId: this.actions?.[this.selectedActionIndex]?.case_action_id?.slice(0,8),
          ac_status: this.actions?.[this.selectedActionIndex]?.ac_status,
        },
        agentUploadQuestionnaireId: this.agentUploadQuestionnaireId,
      });
    }

    this.wizardLoadingService.hide(); // Esconde imediatamente sem delay
    this.loaded = true;
  }

  /**
   * Recarrega as actions do GuideService (força refresh)
   */
  private async reloadActions(): Promise<void> {
    console.log('🔎 [WIZARD_DEBUG] RELOAD_ACTIONS_START', {
      previousActionsCount: this.actions?.length || 0,
      previousOrder: this.actions?.map((a, idx) => ({ step: idx, name: a.action_name, type: a.action_type, ac_status: a.ac_status })),
    });

    // Force o GuideService a buscar novamente
    this.guideService.requirementsFormClaim = null;

    // Recarrega
    await this.guideService.checkIfUserMustAccessGuide('all', this.claimId);

    // Atualiza localmente
    if (this.guideService.requirementsFormClaim) {
      this.actions = [...this.guideService.requirementsFormClaim];
      console.log('🔎 [WIZARD_DEBUG] RELOAD_ACTIONS_DONE', {
        newActionsCount: this.actions.length,
        newOrder: this.actions.map((a, idx) => ({
          step: idx,
          name: a.action_name,
          type: a.action_type,
          caseActionId: a.case_action_id?.slice(0,8),
          prstatus: a.prstatus,
          prerequisite_ids: (a.prerequisite_ids || []).map((id: string) => id.slice(0,8)),
          ac_status: a.ac_status,
        })),
      });
    } else {
      this.actions = undefined;
      console.log('🔎 [WIZARD_DEBUG] RELOAD_ACTIONS_DONE — no actions returned');
    }
  }

  /**
   * Verifica se uma action está realmente completa
   * Para KYC, valida também se passed = true no kyc_check
   */
  private async isActionComplete(action: Action): Promise<boolean> {
    const status = action.ac_status;

    switch (action.action_type) {
      case 'KYC':
        // KYC precisa ter status DONE E passed = true
        if (status !== 'DONE') {
          return false;
        }

        // Valida se realmente passou no KYC check
        if (action.done_action_id) {
          try {
            const kycCheck = await this.kycService.getById(action.done_action_id);

            if (!kycCheck) {
              console.warn('[GUIDE] KYC check not found', {
                actionId: action.action_id,
                kycCheckId: action.done_action_id,
              });
              return false;
            }

            const passed = kycCheck.passed === true;

            // Check if KYC was bypassed by an agent (server-side flag)
            const currentUser = await this.userService.getCurrentUser();
            const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;

            // CPK-3620: FAILED but postponable (notes.postpone_reason.canProceed)
            const canProceed = notesAllowProceed((kycCheck as any).notes);

            // CPK-4051: agent-driven skip (SKIPPED_BY_AGENT + IC_AGENT_FLOW)
            const agentSkip = isAgentSkipCheck(kycCheck as any);

            console.log('[GUIDE] KYC check validated', {
              actionId: action.action_id,
              kycCheckId: action.done_action_id,
              passed,
              bypassedByAgent,
              canProceed,
              agentSkip,
            });

            return passed || bypassedByAgent || canProceed || agentSkip;
          } catch (error) {
            console.error('[GUIDE] Error validating KYC check', {
              actionId: action.action_id,
              error: error instanceof Error ? error.message : String(error),
            });
            return false;
          }
        }

        // Se não tem done_action_id mas status = DONE, considera incompleto
        console.warn('[GUIDE] KYC marked DONE but no kyc_check', {
          actionId: action.action_id,
        });
        return false;

      case 'SIGNATURE':
        // Assinatura só está completa se status = SIGNED
        return status === 'SIGNED';

      case 'QUESTIONNAIRE':
        // Questionário aceita DONE ou SUBMITTED
        return status === 'DONE' || status === 'SUBMITTED';

      default:
        // Para outros tipos, considera completo se tem status DONE
        return status === 'DONE';
    }
  }

  async onComplete() {
    const completedAction = this.actions?.[this.selectedActionIndex];
    console.log('🔎 [WIZARD_DEBUG] STEP_COMPLETED', {
      completedIndex: this.selectedActionIndex,
      completedAction: {
        name: completedAction?.action_name,
        type: completedAction?.action_type,
        caseActionId: completedAction?.case_action_id?.slice(0,8),
        ac_status: completedAction?.ac_status,
      },
      totalActions: this.actions?.length,
      isLastStep: this.selectedActionIndex === (this.actions?.length || 0) - 1,
    });

    if (!this.actions || !this.claimId) {
      return;
    }

    // If in agent skipGuide mode, redirect home after completing the target questionnaire
    if (this.agentUploadQuestionnaireId) {
      console.log('[GUIDE] Agent skipGuide mode — questionnaire completed, redirecting home');
      localStorage.removeItem('agentUploadQuestionnaireId');
      this.agentUploadQuestionnaireId = null;
      this.guideService.setSkipCheckTrue();
      this.router.navigate(['/client/home']);
      return;
    }

    // Check if this is the last step
    const isLastStep = this.selectedActionIndex === (this.actions.length - 1);

    if (isLastStep) {
      console.log('[GUIDE] Last step completed, redirecting to intermediate page for final verification');
      // Redireciona para a página intermediária passando o destino final via query param
      this.router.navigate(['/client/redirecting'], {
        queryParams: { redirectTo: '/client/home?guideCompleted=true' }
      });
      return;
    }

    // 🔄 Inicia loading BETWEEN STEPS
    this.wizardLoadingService.start(
      WizardLoadingService.CommonSteps.BETWEEN_STEPS,
      WizardLoadingMessages.PROCESSING
    );

    // ✅ BETWEEN STEPS: Verificar claim inativo + get state
    console.log('[GUIDE] Between steps - checking claim and getting new state');

    const user = await this.userService.getCurrentUser();
    if (!user?.id) {
      console.error('[GUIDE] No user found during onComplete');
      this.wizardLoadingService.hide();
      return;
    }

    try {
      // 1️⃣ Recarrega actions do GuideService (force refresh)
      await this.reloadActions();
      this.wizardLoadingService.completeStep('reload');

      if (!this.actions || this.actions.length === 0) {
        const claimStillValid = await this.checkClaimStillValid();
        if (!claimStillValid) {
          console.warn('[GUIDE] Claim became INACTIVE during between-steps check, redirecting to home');
          this.wizardLoadingService.hide();
          this.router.navigate(['/client/home'], {
            queryParams: { inactiveClaim: 'true', inactiveClaimId: this.claimId }
          });
          return;
        }
      }

      // 2️⃣ Usa ManagementStepService para verificar claim + próximo estado
      const wizardState = await this.managementStepService.getState(
        this.actions!,
        this.claimId,
        user.id
      );

      this.wizardLoadingService.completeStep('verify_claim');
      this.wizardLoadingService.completeStep('calculate_next');

      console.log('🔎 [WIZARD_DEBUG] NEXT_STEP_STATE', {
        previousIndex: this.selectedActionIndex,
        newIndex: wizardState.currentActionIndex,
        nextAction: {
          name: wizardState.currentAction?.action_name,
          type: wizardState.currentAction?.action_type,
          caseActionId: wizardState.currentAction?.case_action_id?.slice(0,8),
          ac_status: wizardState.currentAction?.ac_status,
          prstatus: wizardState.currentAction?.prstatus,
        },
        allActionsComplete: wizardState.allActionsComplete,
        claimIsActive: wizardState.claimIsActive,
        reloadedActionsOrder: this.actions?.map((a, idx) => ({
          step: idx,
          name: a.action_name,
          type: a.action_type,
          ac_status: a.ac_status,
        })),
      });

      // 3️⃣ Verifica se claim ficou INACTIVE
      if (!wizardState.claimIsActive) {
        console.warn('[GUIDE] Claim became INACTIVE, redirecting to home');
        this.wizardLoadingService.hide();
        this.router.navigate(['/client/home'], {
          queryParams: { inactiveClaim: 'true', inactiveClaimId: this.claimId }
        });
        return;
      }

      // 4️⃣ Verifica se todas as actions estão completas
      if (wizardState.allActionsComplete) {
        console.log('[GUIDE] All requirements completed, redirecting to home');
        this.wizardLoadingService.finish(WizardLoadingMessages.DONE);
        this.router.navigate(['/client/home'], {
          queryParams: { guideCompleted: 'true' }
        });
        return;
      }

      // 5️⃣ Avança para a próxima action incompleta (calculada pelo ManagementStepService)
      console.log('🔎 [WIZARD_DEBUG] MOVING_TO_NEXT_STEP', {
        fromIndex: this.selectedActionIndex,
        toIndex: wizardState.currentActionIndex,
        nextActionId: wizardState.currentAction.action_id,
        nextActionType: wizardState.currentAction.action_type,
      });

      this.wizardLoadingService.finish(WizardLoadingMessages.NEXT_STEP);
      this.selectedActionIndex = wizardState.currentActionIndex;
      this.tutorialVideoService.setStepTimestamp(this.resolveStepTimestamp(this.selectedActionIndex));
    } catch (error) {
      console.error('[GUIDE] Error during between-steps check', error);
      this.wizardLoadingService.hide();
      this.router.navigateByUrl('/client/home');
    }
  }

  /**
   * Verifica se a claim ainda é válida (não foi marcada como INACTIVE)
   */
  private async checkClaimStillValid(): Promise<boolean> {
    if (!this.claimId) {
      return false;
    }

    try {
      const claimData = await this.claimsService.getClaimWithCase(this.claimId);

      if (!claimData) {
        console.warn('[GUIDE] Claim not found', { claimId: this.claimId });
        return false;
      }

      const isActive = claimData.status !== 'INACTIVE';

      console.log('[GUIDE] Claim status checked', {
        claimId: this.claimId,
        status: claimData.status,
        isActive,
      });

      return isActive;
    } catch (error) {
      console.error('[GUIDE] Error checking claim validity', {
        claimId: this.claimId,
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Handler para clique nas bolinhas de step
   * Só permite navegar para steps anteriores ou atual
   */
  onStepClick(index: number): void {
    // Só permite clicar em steps anteriores ou atual
    if (index > this.selectedActionIndex) {
      console.warn('🔎 [WIZARD_DEBUG] STEP_CLICK_BLOCKED', {
        attemptedIndex: index,
        currentIndex: this.selectedActionIndex,
        attemptedAction: this.actions?.[index] ? {
          name: this.actions[index].action_name,
          type: this.actions[index].action_type,
        } : 'N/A',
      });
      return;
    }

    console.log('🔎 [WIZARD_DEBUG] STEP_CLICK_NAVIGATE', {
      fromIndex: this.selectedActionIndex,
      toIndex: index,
      fromAction: this.actions?.[this.selectedActionIndex] ? {
        name: this.actions[this.selectedActionIndex].action_name,
        type: this.actions[this.selectedActionIndex].action_type,
      } : 'N/A',
      toAction: this.actions?.[index] ? {
        name: this.actions[index].action_name,
        type: this.actions[index].action_type,
        ac_status: this.actions[index].ac_status,
      } : 'N/A',
    });

    this.selectedActionIndex = index;
  }

  async openSuccessModal() {
    console.log('[GUIDE] Opening success modal', {
      claimId: this.claimId,
    });

    this.guideService.setSkipCheckTrue();

    const claimData = await this.claimsService.getClaimWithCase(this.claimId!);

    console.log('[GUIDE] Claim data loaded for success modal', {
      claimId: claimData?.id,
      caseId: claimData?.case_id,
    });

    this.dialog.open(AllDoneDialogComponent, {
      data: { claim: claimData },
      disableClose: true,
    });
  }
}
